import { useEffect, useRef, useState } from "react";

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatQQ = (raw) => {
  if (!raw) return "";
  try {
    const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(arr) ? arr.map((q) => `${q.label} | ${q.text}`).join("\n") : "";
  } catch {
    return "";
  }
};

const parseQQ = (str) =>
  str.split("\n").filter((l) => l.trim()).map((line) => {
    const idx = line.indexOf("|");
    if (idx === -1) return { label: line.trim(), text: line.trim() };
    return { label: line.slice(0, idx).trim(), text: line.slice(idx + 1).trim() };
  });

const EMPTY = {
  navLabel: "", routePath: "",
  navIcon: "",
  agentIdentifier: "", directConnectUrl: "",
  quickQuestions: "", isActive: true, allowFileUpload: false,
};

// ── Image upload field ────────────────────────────────────────────────────────

const MAX_IMG_KB = 200;
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/svg+xml", "image/webp", "image/gif"];

function ImageUploadField({ label, value, onChange, disabled }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadErr("Only PNG, JPEG, SVG, WebP, or GIF allowed.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMG_KB * 1024) {
      setUploadErr(`Max ${MAX_IMG_KB} KB. This file is ${(file.size / 1024).toFixed(0)} KB.`);
      e.target.value = "";
      return;
    }
    setUploadErr(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await fetch("/api/upload-image", { method: "POST", body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Upload failed.");
      onChange(j.path);
    } catch (ex) {
      setUploadErr(ex.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div style={{ marginBottom: 4 }}>
      <label className="bot-form__label">{label}</label>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <img
          src={value || "/images/robot.png"}
          alt="preview"
          style={{ width: 30, height: 30, borderRadius: 4, objectFit: "contain",
            border: "1px solid #e2e8f0", background: "#f8fafc", flexShrink: 0 }}
        />
        <input
          className="triage-search"
          value={value}
          onChange={(e) => { setUploadErr(null); onChange(e.target.value); }}
          placeholder="/images/goldfish-1.png"
          disabled={disabled || uploading}
          style={{ flex: 1, boxSizing: "border-box" }}
        />
        <input
          ref={fileRef}
          type="file"
          accept={ALLOWED_TYPES.join(",")}
          style={{ display: "none" }}
          onChange={handleFile}
        />
        <button
          type="button"
          className="pagination-btn"
          onClick={() => { setUploadErr(null); fileRef.current?.click(); }}
          disabled={disabled || uploading}
          style={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
      <p className="bot-form__hint">PNG · JPEG · SVG · WebP · GIF &nbsp;·&nbsp; max {MAX_IMG_KB} KB</p>
      {uploadErr && (
        <p style={{ color: "#dc2626", fontSize: "0.75rem", margin: "2px 0 0" }}>{uploadErr}</p>
      )}
    </div>
  );
}

// ── Inline add / edit form ────────────────────────────────────────────────────

function BotForm({ initial, isEdit, onSave, onCancel }) {
  const [form, setForm]   = useState(initial || EMPTY);
  const [err, setErr]     = useState(null);
  const [busy, setBusy]   = useState(false);

  const field = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const check = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.checked }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.navLabel.trim())         { setErr("Display Name is required.");        return; }
    if (!form.routePath.trim())        { setErr("Route Path is required.");          return; }
    if (!form.directConnectUrl.trim()) { setErr("Direct Connect URL is required.");  return; }
    if (!form.agentIdentifier.trim()) { setErr("Agent Identifier is required.");     return; }
    setBusy(true);
    try {
      await onSave({ ...form, quickQuestions: JSON.stringify(parseQQ(form.quickQuestions)) });
    } catch (ex) {
      setErr(ex.message || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const inp = (key, placeholder, opts = {}) => (
    <input
      className="triage-search"
      value={form[key]}
      onChange={field(key)}
      disabled={opts.disabled || busy}
      placeholder={placeholder}
      style={{ width: "100%", boxSizing: "border-box" }}
    />
  );

  return (
    <form onSubmit={submit} noValidate className="bot-form">
      <div className="bot-form__grid">

        <div>
          <label className="bot-form__label">Display Name <span className="bot-form__req">*</span></label>
          {inp("navLabel", "e.g. Peoplehub")}

          <label className="bot-form__label">Route Path <span className="bot-form__req">*</span></label>
          {inp("routePath", "e.g. /chat/peoplehub")}

          

          <label className="bot-form__label">
            Quick Questions
            <span className="bot-form__hint" style={{ marginLeft: 6 }}>Label | Full question (one per line)</span>
          </label>
          <textarea
            className="triage-search"
            value={form.quickQuestions}
            onChange={field("quickQuestions")}
            disabled={busy}
            rows={14}
            style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
            placeholder={"Reset password | How do I reset my network password?\nSetup VPN | How to setup VPN?"}
          />
        </div>

        <div>
          <label className="bot-form__label">Direct Connect URL <span className="bot-form__req">*</span></label>
          {inp("directConnectUrl", "https://...powerplatform.com/copilotstudio/...")}

          <label className="bot-form__label">Agent Identifier <span className="bot-form__req">*</span></label>
          {inp("agentIdentifier", "e.g. auto_agent_LqM_x")}

          <ImageUploadField
            label="Icon"
            value={form.navIcon}
            onChange={(v) => setForm((f) => ({ ...f, navIcon: v }))}
            disabled={busy}
          />

        </div>
      </div>

      {err && <p className="ur-add-form__error">{err}</p>}

      <div className="triage-filter" style={{ marginTop: 12 }}>
        <button type="submit" className="add-btn" disabled={busy}>
          {busy ? "Saving…" : isEdit ? "Update Agent" : "Add Agent"}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
        <label className="bot-form__active-toggle">
          <input type="checkbox" checked={form.isActive} onChange={check("isActive")} disabled={busy} />
          Active
        </label>
        <label className="bot-form__active-toggle">
          <input type="checkbox" checked={!!form.allowFileUpload} onChange={check("allowFileUpload")} disabled={busy} />
          Allow File Upload
        </label>
      </div>
    </form>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function BotConfigAdmin({ onConfigChange }) {
  const [bots, setBots]             = useState([]);
  const [search, setSearch]         = useState("");
  const [loading, setLoading]       = useState(true);
  const [fetchErr, setFetchErr]     = useState(null);
  const [showAdd, setShowAdd]       = useState(false);
  const [editing, setEditing]       = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [sortCol, setSortCol]       = useState(null);
  const [sortDir, setSortDir]       = useState("asc");
  const [page, setPage]             = useState(1);
  const [pageSize, setPageSize]     = useState(10);

  useEffect(() => {
    fetch("/api/bot-configs")
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((j) => { setBots(j.data || []); setLoading(false); })
      .catch((e) => { setFetchErr(e.message); setLoading(false); });
  }, []);

  const saveNew = async (payload) => {
    const r = await fetch("/api/bot-configs", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const j = await r.json();
    if (!r.ok) throw new Error(j.error || "Failed to add bot.");
    setBots((p) => [...p, j.data]);
    setShowAdd(false);
    onConfigChange?.();
  };

  const saveEdit = async (payload) => {
    const r = await fetch(`/api/bot-configs/${editing.Id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const j = await r.json();
    if (!r.ok) throw new Error(j.error || "Failed to update bot.");
    setBots((p) => p.map((b) => (b.Id === editing.Id ? j.data : b)));
    setEditing(null);
    onConfigChange?.();
  };

  const toggleActive = async (bot) => {
    setTogglingId(bot.Id);
    try {
      const r = await fetch(`/api/bot-configs/${bot.Id}/active`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: !bot.IsActive }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Update failed.");
      setBots((p) => p.map((b) => (b.Id === bot.Id ? j.data : b)));
      onConfigChange?.();
    } catch (e) {
      alert(e.message);
    } finally {
      setTogglingId(null);
    }
  };

  const handleSort = (col) => {
    setSortDir(sortCol === col ? (sortDir === "asc" ? "desc" : "asc") : "asc");
    setSortCol(col);
    setPage(1);
  };

  const si = (col) => sortCol !== col ? " ⇅" : sortDir === "asc" ? " ↑" : " ↓";

  const filtered = bots.filter((b) =>
    [b.AgentKey, b.NavLabel, b.RoutePath]
      .some((v) => String(v ?? "").toLowerCase().includes(search.toLowerCase()))
  );

  const sorted = [...filtered].sort((a, b) => {
    if (!sortCol) return 0;
    const c = String(a[sortCol] ?? "").localeCompare(String(b[sortCol] ?? ""), undefined, { numeric: true });
    return sortDir === "asc" ? c : -c;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageData   = sorted.slice((page - 1) * pageSize, page * pageSize);
  const isEmpty    = !loading && !fetchErr && bots.length === 0;

  if (loading) return (
    <div className="text-center">
      <img src="/images/lnw-loader.gif" alt="Loading.." />
      <h2 className="text-center">Loading..!</h2>
    </div>
  );

  if (fetchErr) return <div className="triage-error">Error: {fetchErr}</div>;

  return (
    <div className="p-3">
      <h3>Agent Configuration</h3>

      {/* Filter bar */}
      <div className="triage-filter">
        <input
          className="triage-search"
          type="search"
          placeholder="🔍  Search by key, name or route…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <button type="button" className="add-btn"
          onClick={() => { setShowAdd((v) => !v); setEditing(null); }}>
          {showAdd ? "Cancel" : "+ Add Agent"}
        </button>
      </div>

      {/* Add form */}
      {showAdd && !editing && (
        <BotForm isEdit={false} onSave={saveNew} onCancel={() => setShowAdd(false)} />
      )}

      {/* Edit form */}
      {editing && (
        <BotForm
          isEdit
          initial={{
            navLabel:         editing.NavLabel,
            routePath:        editing.RoutePath,
            navIcon:          editing.NavIcon          || "",
            agentIdentifier:  editing.AgentIdentifier  || "",
            directConnectUrl: editing.DirectConnectUrl || "",

            quickQuestions:   formatQQ(editing.QuickQuestions),
            isActive:         !!editing.IsActive,
            allowFileUpload:  !!editing.AllowFileUpload,
          }}
          onSave={saveEdit}
          onCancel={() => setEditing(null)}
        />
      )}

      {/* Empty state */}
      {isEmpty && !showAdd && (
        <div className="triage-empty-state">
          <p className="no-records">No agents configured.</p>
          <p className="no-records-subtitile">Use "+ Add Agent" to register the first agent.</p>
        </div>
      )}

      {/* Table */}
      {!isEmpty && (
        <div className="table-wrapper">
          <table className="triage-table">
            <thead>
              <tr>
                {[["AgentKey","Agent Key"],["NavLabel","Display Name"],["RoutePath","Route Path"]].map(([col, lbl]) => (
                  <th key={col} onClick={() => handleSort(col)} style={{ cursor: "pointer" }}>
                    {lbl}<span className="sort-icon">{si(col)}</span>
                  </th>
                ))}
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 ? (
                <tr><td colSpan={6} className="text-center">No matching records.</td></tr>
              ) : pageData.map((bot) => (
                <tr key={bot.Id} className="triage-row">
                  <td className="triage-td"><code style={{ fontSize: "0.8rem" }}>{bot.AgentKey}</code></td>
                  <td className="triage-td">{bot.NavLabel}</td>
                  <td className="triage-td">{bot.RoutePath}</td>
                  <td className="triage-td">
                    <button
                      type="button"
                      className={`agent-toggle${bot.IsActive ? " is-active" : ""}`}
                      title={bot.IsActive ? "Click to deactivate" : "Click to activate"}
                      disabled={togglingId === bot.Id}
                      onClick={() => toggleActive(bot)}
                    >
                      <span className="agent-toggle__knob" />
                      <span className="agent-toggle__label">
                        {togglingId === bot.Id ? "…" : bot.IsActive ? "ON" : "OFF"}
                      </span>
                    </button>
                  </td>
                  <td className="triage-td">
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      {/* Edit */}
                      <button type="button" className="pagination-btn" title="Edit"
                        style={{ padding: "4px 6px", lineHeight: 0 }}
                        onClick={() => { setEditing(bot); setShowAdd(false); }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="d-flex p-3">
        {!isEmpty && totalPages > 1 && (
          <div className="triage-pagination">
            <button onClick={() => setPage(1)} disabled={page === 1}>«</button>
            <button onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1)
                  acc.push(<span key={`e${p}`} className="pg-info">…</span>);
                acc.push(
                  <button key={p} className={`pagination-btn ${p === page ? "active" : ""}`}
                    onClick={() => setPage(p)}>{p}</button>
                );
                return acc;
              }, [])}
            <button onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>›</button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
            <span className="pg-info">
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, sorted.length)} of {sorted.length}
            </span>
          </div>
        )}
        <div className="triage-row-per-page">
          <label className="page-size-label">Rows per page:</label>
          <select className="triage-select" value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
            {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}